import { useNavigation, useRoute } from '@react-navigation/native';
import fetch from 'cross-fetch';
import { useFormikContext } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, TouchableOpacity } from 'react-native';

import { Box, ShadowBox, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { Spinner } from '@components/shared/Spinner';
import { RegisterStackScreenProps } from '@navigation/auth/register-stack-new.web';
import theme from '@themes/theme';
import { getApiConfig } from '@utils/getApiConfig';

import { RegisterFormValues } from './RegisterForm';
import VerificationCodeInput from './VerificationCodeInput';

const apiConfig = getApiConfig();
const EMAIL_CODE_LENGTH = 6;
const RESEND_SECONDS = 30;

const RegisterVerifyEmailWeb: React.FC = () => {
  const navigation =
    useNavigation<
      RegisterStackScreenProps<'register-verify-email'>['navigation']
    >();
  const route =
    useRoute<RegisterStackScreenProps<'register-verify-email'>['route']>();
  const { t } = useTranslation('models');
  const { values, setFieldValue, errors, setErrors } =
    useFormikContext<RegisterFormValues>();

  const email = route.params?.email || values.email;
  const [countdown, setCountdown] = useState(RESEND_SECONDS);
  const [isActive, setIsActive] = useState(true);
  // Separate "sending" (Resend code) and "verifying" (Verify Email button)
  // states — each control shows its own spinner (TA-10089), not a single
  // shared indicator for either action.
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [screenError, setScreenError] = useState<string | null>(null);
  const [hasVerificationError, setHasVerificationError] = useState(false);
  const [hasResentCode, setHasResentCode] = useState(false);
  const isResendDisabled =
    isSendingCode || isVerifying || (isActive && countdown > 0);

  const isCodeComplete = useMemo(
    () => values.verify_code?.length === EMAIL_CODE_LENGTH,
    [values.verify_code]
  );
  const isVerifyDisabled =
    !isCodeComplete || isSendingCode || isVerifying || hasVerificationError;

  // Pick up OTP error set by RegisterForm submit handler when navigated back
  // after a failed registration attempt (e.g. expired/invalid code).
  useEffect(() => {
    if (values.verify_code_server_error) {
      setScreenError(values.verify_code_server_error);
      setHasVerificationError(true);
      setFieldValue('verify_code_server_error', '');
    }
  }, [values.verify_code_server_error]);

  const sendEmailVerificationCode = async () => {
    if (!email) {
      setScreenError(t('registerVerifyEmail.emailRequired'));
      return false;
    }

    setIsSendingCode(true);
    setScreenError(null);
    setHasVerificationError(false);
    setHasResentCode(false);
    setFieldValue('verify_code', '');

    try {
      const authEndpoint = `${
        apiConfig.apiUrl
      }/api/v4/send_email_verification_code.json?email=${encodeURIComponent(
        email.toLowerCase()
      )}`;
      const response = await fetch(authEndpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      const json = await response.json();

      if (json?.error) {
        setScreenError(json.error);
        return false;
      }

      if (json?.success === false) {
        setScreenError(t('registerVerifyEmail.unableToSendVerificationCode'));
        return false;
      }

      setCountdown(RESEND_SECONDS);
      setIsActive(true);
      setHasResentCode(true);
      return true;
    } catch {
      setScreenError(t('registerVerifyEmail.networkError'));
      return false;
    } finally {
      setIsSendingCode(false);
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (isActive && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown((current) => current - 1);
      }, 1000);
    } else if (countdown === 0) {
      setIsActive(false);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [countdown, isActive]);

  const handleContinue = () => {
    if (!isCodeComplete) {
      setScreenError(t('registerVerifyEmail.enterSixDigitCode'));
      return;
    }

    const checkEmailVerificationCode = async () => {
      setIsVerifying(true);
      setScreenError(null);
      setHasVerificationError(false);
      setHasResentCode(false);

      try {
        const authEndpoint = `${apiConfig.apiUrl}/api/v4/check_email_verification_code.json`;
        const response = await fetch(authEndpoint, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email.toLowerCase(),
            code: values.verify_code,
          }),
        });
        const json = await response.json();

        if (json?.success === true || json?.success === 'true') {
          setErrors({
            ...errors,
            verify_code: undefined,
          });
          navigation.navigate('create-profile', {
            emailVerificationCode: values.verify_code,
          });
          return;
        }

        setHasVerificationError(true);
        let error = json?.error ?? '';
        if (error.toString().startsWith('Invalid')) {
          error = t('registerVerifyEmail.incorrectCode');
        }
        setScreenError(error);
      } catch {
        setHasVerificationError(true);
        setScreenError(t('registerVerifyEmail.incorrectCode'));
      } finally {
        setIsVerifying(false);
      }
    };

    setErrors({
      ...errors,
      verify_code: undefined,
    });
    checkEmailVerificationCode();
  };

  return (
    <Box
      flex={1}
      backgroundColor='grey01'
      alignItems='center'
      justifyContent='center'>
      <ShadowBox
        variant='base'
        backgroundColor='white'
        borderRadius='m'
        style={{
          width: 492,
        }}>
        <Box
          alignItems='center'
          borderRadius='m'
          px='xl'
          mx='xxs'
          py='l'
          my='xs'
          backgroundColor='white'>
          <Icon name='Logo' width={142} height={43} marginBottom='xl' />

          <Text variant='heading2' color='textPrimary' textAlign='center'>
            {t('registerVerifyEmail.title')}
          </Text>
          <Text
            variant='webBodySecondary'
            color='textSecondary'
            textAlign='center'
            marginTop='xs'
            marginBottom='m'>
            {t('registerVerifyEmail.subtitle')}
          </Text>
          <Box
            alignSelf='center'
            backgroundColor='grey01'
            borderRadius='xs'
            paddingHorizontal='s'
            paddingVertical='xs'
            marginBottom='xl'>
            <Text variant='labelSmall' color='textSecondary'>
              {email}
            </Text>
          </Box>

          <Box minHeight={64} marginBottom='m' width='100%' alignItems='center'>
            <VerificationCodeInput
              inputSize={EMAIL_CODE_LENGTH}
              hasError={hasVerificationError}
              forceSuccessState={hasResentCode}
              disabled={isSendingCode || isVerifying}
              onTextChanged={(text) => {
                setFieldValue('verify_code', text);
                if (screenError || hasVerificationError || hasResentCode) {
                  setHasVerificationError(false);
                  setScreenError(null);
                  setHasResentCode(false);
                }
              }}
            />
            {screenError && (
              <Text variant='bodySecondary' color='alertRed' marginTop='s'>
                {screenError}
              </Text>
            )}
          </Box>

          <Box flexDirection='row' alignItems='center'>
            <TouchableOpacity
              testID='resend-email-code-button-web'
              disabled={isResendDisabled}
              style={{ alignSelf: 'center' }}
              onPress={async () => {
                const success = await sendEmailVerificationCode();
                if (!success) {
                  Alert.alert(
                    t('registerVerifyEmail.sendEmailCodeTitle'),
                    t('registerVerifyEmail.unableToSendVerificationCode')
                  );
                }
              }}>
              <Text
                variant='labelSmallBold'
                color={isResendDisabled ? 'grey04' : 'greenSecondary'}>
                {isActive && countdown > 0
                  ? t('registerVerifyEmail.resendCodeIn', {
                      time: `00:${countdown.toString().padStart(2, '0')}`,
                    })
                  : t('registerVerifyEmail.resendCode')}
              </Text>
            </TouchableOpacity>
            {isSendingCode && (
              <Box marginLeft='xs'>
                <Spinner
                  size={14}
                  strokeWidth={2}
                  color={theme.colors.greenSecondary}
                />
              </Box>
            )}
          </Box>

          <Box width='100%' marginTop='xl'>
            <Button
              variant='primary'
              fullWidth
              isSmall
              disabled={isVerifyDisabled}
              isLoading={isVerifying}
              onPress={handleContinue}
              accessibilityLabel={t('registerVerifyEmail.verifyEmailButton')}>
              {t('registerVerifyEmail.verifyEmailButton')}
            </Button>
          </Box>
        </Box>
      </ShadowBox>
    </Box>
  );
};

export default RegisterVerifyEmailWeb;
